import { color } from 'd3-color'
import React from 'react'
import { Chart } from 'react-chartjs-2'

import { useDatasetStyleProps, IChartJsDatasetStyles } from 'src/commons/chartjs'
import { getTranslation } from 'src/commons/i18n'

import { GranularityEnum, IGenericBucket } from '../types'
import { getUniqueCategories } from '../utils'

import {
  buildHistogramOptions,
  getChartPlugins,
  getLabelsWithGranularity,
  IGraphProps,
  STYLE_CHART_CONTAINER,
} from './graphCommons'
import { useGraphContext } from './GraphWrapper/GraphWrappers'
import { DEFAULT_COLORS } from './themes'

const getDocCount = (bucket: IGenericBucket): number => bucket.doc_count || 0

function transparentize(value: any, opacity: number) {
  const col = color(value)
  if (col === null) {
    console.error(`Received invalid color ${value}`)
    return 'black'
  }
  return col.copy({ opacity: opacity }).formatRgb()
}

export function getDatasets(
  bucketsReferenceYear: IGenericBucket[],
  bucketsPreviousYear: IGenericBucket[],
  datasetStylesMapping: Record<string, IChartJsDatasetStyles>,
  formatLabel: (x: any) => string = (x: any) => x
) {
  const { t } = getTranslation()
  const previousYearSuffix = t('previous year')
  const stackedBucketsReferenceYear = bucketsReferenceYear
    .map((b: any): any => b.stackedBy && b.stackedBy.buckets)
    .filter(Boolean)
  const stackedBucketsPreviousYear = bucketsPreviousYear
    .map((b: any): any => b.stackedBy && b.stackedBy.buckets)
    .filter(Boolean)
  const uniqueCategoriesReferenceYear = getUniqueCategories(stackedBucketsReferenceYear)
  const uniqueCategoriesPreviousYear = getUniqueCategories(stackedBucketsPreviousYear)
  const uniqueCategories = [...new Set([...uniqueCategoriesReferenceYear, ...uniqueCategoriesPreviousYear])].sort()

  const datasets: any[] = uniqueCategories
    .map((category: string) => {
      const datasetStyles = datasetStylesMapping[category]
      return [
        {
          ...datasetStyles,
          label: `${formatLabel(category)} (${previousYearSuffix})`,
          baseLabel: category,
          stack: 'previous_year',
          data: stackedBucketsPreviousYear.map((bucket: any[]): number =>
            getDocCount(bucket.find((b: IGenericBucket) => b.key === category) || {})
          ),
          backgroundColor: transparentize(datasetStyles.backgroundColor, 0.1),
          borderColor: transparentize(datasetStyles.borderColor, 0.1),
          hoverBackgroundColor: transparentize(datasetStyles.hoverBackgroundColor, 0.6),
          hoverBorderColor: transparentize(datasetStyles.hoverBorderColor, 0.6),
        },
        {
          ...datasetStyles,
          label: formatLabel(category),
          baseLabel: category,
          stack: 'reference_year',
          data: stackedBucketsReferenceYear.map((bucket: any[]): number =>
            getDocCount(bucket.find((b: IGenericBucket) => b.key === category) || {})
          ),
        },
      ]
    })
    .flat()

  return datasets
}

interface IProps extends IGraphProps {
  adjacentBars?: boolean
  enableAnimation?: boolean
  formatLabel?: (x: any) => string
  granularity: GranularityEnum
  rawDataPreviousYear: any[]
}

function TemporalStackedHistogramWithPreviousYear({
  title = '',
  showTitle,
  w = 600,
  h = 300,
  rawData,
  rawDataPreviousYear,
  formatWithUnit,
  granularity,
  adjacentBars,
  maxValue = 0,
  enableAnimation = true,
  xTicksFormatter,
  datasetCategory,
  formatLabel,
}: IProps) {
  const { reactChart } = useGraphContext()

  const labels: string | string[] = getLabelsWithGranularity(rawData, granularity)

  const datasetStylesMapping = useDatasetStyleProps(datasetCategory, DEFAULT_COLORS)
  const datasets = getDatasets(rawData, rawDataPreviousYear, datasetStylesMapping, formatLabel)
  // We show the legend if there are more than 1 datasets to show on the graph
  const displayLegend = datasets.length > 1
  const options = buildHistogramOptions({
    title,
    showTitle,
    maxValue,
    enableAnimation,
    formatWithUnit,
    adjacentBars,
    displayLegend,
    xTicksFormatter,
    horizontal: false,
  })

  const plugins = getChartPlugins()

  return (
    <div style={STYLE_CHART_CONTAINER}>
      <Chart
        title={title}
        ref={reactChart.chartRef}
        type="bar"
        data={{ labels, datasets }}
        width={w}
        height={h}
        options={options}
        plugins={plugins}
      />
    </div>
  )
}

export default TemporalStackedHistogramWithPreviousYear
